import { useState, useEffect, useRef, useMemo } from 'react';
import { GenericColumn } from '../utils/tableColumns';
import { DatePresetId, findPresetById } from '../utils/datePresets';
import { DateRangeFilterState, FilterState, SavePreferenceSettings, SortConfig, StringFilterOperation, UseTableFiltersResult } from '../../../types/tableFilterSettings';
import { usePreferences } from '../../../context/PreferencesContext';

// Helper function to check if string is an advanced filter and parse it
const parseAdvancedFilter = (filter: string): { operation: StringFilterOperation; value: string } | null => {
  const operations: StringFilterOperation[] = ['contains', 'equals', 'startsWith', 'endsWith'];

  for (const op of operations) {
    if (filter.startsWith(`${op}:`)) {
      return {
        operation: op,
        value: filter.substring(op.length + 1)
      };
    }
  }

  return null;
};

// Helper function to apply advanced string filter
const applyStringFilter = (value: string, filter: string): boolean => {
  const advancedFilter = parseAdvancedFilter(filter);

  if (!advancedFilter) {
    // Regular filter - simple inclusion check
    return value.includes(filter);
  }

  const { operation, value: filterValue } = advancedFilter;

  switch (operation) {
    case 'contains':
      return value.toLowerCase().includes(filterValue.toLowerCase());
    case 'equals':
      return value.toLowerCase() === filterValue.toLowerCase();
    case 'startsWith':
      return value.toLowerCase().startsWith(filterValue.toLowerCase());
    case 'endsWith':
      return value.toLowerCase().endsWith(filterValue.toLowerCase());
    default:
      return false;
  }
};

export const useTableFilters = <T,>(
  data: T[],
  columns: GenericColumn<T>[],
  defaultSortKey: string = 'createdAt',
  savePreferenceSettings: SavePreferenceSettings,
): UseTableFiltersResult<T> => {
  const { preferences, updatePreference } = usePreferences();
  const preferenceKey = savePreferenceSettings.key;
  const savedPreferences: {
    sortConfig?: SortConfig;
    filterState?: FilterState;
    dateRangeFilters?: DateRangeFilterState;
    activeFilter?: string | null;
  } = preferences?.tableFilterSettings?.[preferenceKey] || {};

  const [sortConfig, setSortConfig] = useState<SortConfig>(savedPreferences.sortConfig || {
    key: defaultSortKey,
    direction: 'desc',
  });
  const [filterState, setFilterState] = useState<FilterState>(savedPreferences.filterState || {});
  const [dateRangeFilters, setDateRangeFilters] = useState<DateRangeFilterState>(savedPreferences.dateRangeFilters || {});
  const [activeFilter, setActiveFilter] = useState<string | null>(savedPreferences.activeFilter || null);
  const filterPopoverRef = useRef<HTMLDivElement>(null);

  // Persist state updates to localStorage
  useEffect(() => {
    if(!savePreferenceSettings.isEnable) return;
    updatePreference({
      tableFilterSettings: {
        [preferenceKey]: {
          sortConfig,
          filterState,
          dateRangeFilters,
          activeFilter,
        }
      }
    });
  }, [sortConfig, filterState, dateRangeFilters, activeFilter]);

  useEffect(() => {
    // Function to handle clicks outside the filter popover
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPopoverRef.current &&
        !filterPopoverRef.current.contains(event.target as Node) &&
        activeFilter !== null) {
        setActiveFilter(null);
      }
    };

    // Add event listener when a filter is active
    if (activeFilter !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Clean up the event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeFilter]);

  const handleFilterChange = (columnKey: string, value: string) => {
    setFilterState(prev => {
      const currentFilters = prev[columnKey] || [];

      // If value is already in filter, remove it
      if (currentFilters.includes(value)) {
        const newFilters = currentFilters.filter(filter => filter !== value);
        if (newFilters.length === 0) {
          const { [columnKey]: _, ...rest } = prev;
          return rest;
        }
        return { ...prev, [columnKey]: newFilters };
      }

      // Otherwise add it
      return {
        ...prev,
        [columnKey]: [...currentFilters, value]
      };
    });
  };

  // Rest of the state management functions
  const handleDateRangeFilterChange = (columnKey: string, type: 'startDate' | 'endDate', value: string) => {
    setDateRangeFilters(prev => ({
      ...prev,
      [columnKey]: {
        ...prev[columnKey] || { startDate: null, endDate: null },
        [type]: value || null
      }
    }));
  };

  const clearDateRangeFilter = (columnKey: string) => {
    setDateRangeFilters(prev => {
      const { [columnKey]: _, ...rest } = prev;
      return rest;
    });
  };

  const applyPresetDateRange = (columnKey: string, presetId: DatePresetId) => {
    const preset = findPresetById(presetId);
    if (preset) {
      setDateRangeFilters(prev => ({
        ...prev,
        [columnKey]: preset.getDateRange()
      }));
    }
  };

  const handleClearAllFilters = () => {
    setFilterState({});
    setDateRangeFilters({});
  };

  const handleSort = (key: string) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const filteredOrders = useMemo(() => {
    if (Object.keys(filterState).length === 0 && Object.keys(dateRangeFilters).length === 0) {
      return data;
    }

    return data.filter(order => {
      // Filter by regular and advanced filters
      const passesRegularFilters = Object.entries(filterState).every(([columnKey, selectedFilters]) => {
        const column = columns.find(c => c.key === columnKey);

        if (!column || !column.getFilterValue) {
          return true;
        }

        const orderValue = column.getFilterValue(order);

        if (selectedFilters.length === 0) {
          return true;
        }

        return selectedFilters.some(filter => {
          if (typeof orderValue === 'string') {
            // Apply string filters with advanced operations
            return applyStringFilter(orderValue, filter);
          }
          // For non-string values, use exact matching
          return typeof orderValue === 'string' && orderValue === filter;
        });
      });

      if (!passesRegularFilters) return false;

      // Filter by date ranges
      return Object.entries(dateRangeFilters).every(([columnKey, { startDate, endDate }]) => {
        const column = columns.find(c => c.key === columnKey);
        let dateToCheck: Date | null = null;

        // If column has a getFilterValue function, use it to get the date value
        if (column && column.getFilterValue) {
          const dateValue = column.getFilterValue(order);
          if (dateValue && dateValue !== '-') {
            try {
              dateToCheck = new Date(dateValue);
              // Check if date is valid
              if (isNaN(dateToCheck.getTime())) {
                dateToCheck = null;
              }
            } catch (error) {
              dateToCheck = null;
            }
          }
        }

        if (!dateToCheck) {
          // If no valid date to check, pass filter if we're not looking for specific dates
          return !startDate && !endDate;
        }

        // Check startDate condition if present
        if (startDate && dateToCheck < new Date(startDate)) {
          return false;
        }

        // Check endDate condition if present
        if (endDate) {
          // Set end date to end of the day for inclusive filtering
          const endOfDay = new Date(endDate);
          endOfDay.setHours(23, 59, 59, 999);
          if (dateToCheck > endOfDay) {
            return false;
          }
        }

        return true;
      });
    });
  }, [data, filterState, dateRangeFilters, columns]);

  const sortedOrders = useMemo(() => {
    const sorted = filteredOrders ? [...filteredOrders] : [];
    sorted.sort((a, b) => {
      const column = columns.find(c => c.key === sortConfig.key);
      if (!column) return 0;

      if (column.sortFunction) {
        return column.sortFunction(a, b) * (sortConfig.direction === 'asc' ? 1 : -1);
      }

      let aValue: any = (a as any)[sortConfig.key as keyof T];
      let bValue: any = (b as any)[sortConfig.key as keyof T];

      if (aValue === bValue) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      const comparison = aValue > bValue ? 1 : -1;
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredOrders, sortConfig, columns]);

  return {
    sortedOrders,
    filterState,
    dateRangeFilters,
    activeFilter,
    filterPopoverRef,
    sortConfig,
    setActiveFilter,
    handleFilterChange,
    handleDateRangeFilterChange,
    clearDateRangeFilter,
    applyPresetDateRange,
    handleClearAllFilters,
    handleSort,
  };
};
